import { ComponentStyleProps } from "../../../../../lib/styles/props/component-style-props";
import React, { useMemo } from "react";
import {
  Box,
  ComponentMultiStyleConfig,
  Flex,
  HStack,
  Icon,
  Text,
  useMultiStyleConfig,
  VStack,
} from "@chakra-ui/react";
import { CMButtonFileUpload } from "../../../button";
import { CMFileUploadItem } from "../../../../file-upload";
import {
  FileUploadSectionContent,
  FileUploadSubSectionContent,
} from "../types";
import { FileUploadItemContent } from "../../../../file-upload/types";
import { hasValue } from "../../../../../lib/util/common-util";
import { GiPlainCircle } from "react-icons/gi";
import { CMFileUploadTitle, CMFormControlHeader } from "../../../index";
import { SystemStyleObject } from "@chakra-ui/styled-system";

export type CMFormFileUploadProps = {
  value: FileUploadSectionContent;

  /**
   * ファイルが追加されたときに、その追加ファイルを引数として呼び出される。
   * この関数では、ファイルのアップロード処理等を行うことが期待される。
   */
  onAddFile: (sectionId: string, subSectionId: string, file: File) => void;

  // /**
  //  * ファイル削除の確認要求
  //  * @param fileId ファイルID
  //  */
  // onConfirmDelete?: (fileId: string) => Promise<boolean>;

  /**
   * ファイルが削除されたときに、その削除ファイルを引数として呼び出される。
   * @param sectionId セクションID
   * @param subSectionId サブセクションID
   * @param fileId ファイルID
   */
  onDeleteFile: (
    sectionId: string,
    subSectionId: string,
    fileId: string,
  ) => void;

  /**
   * ファイルが選択されたときに、そのファイルを引数として呼び出される。
   * @param fileId
   * @param fileName ファイル名
   */
  onDownloadFile?: (fileId: string, fileName: string) => void;
} & ComponentStyleProps;

/**
 * 添付資料アップロードフォームを表示するコンポーネント
 */
export const CMFormFileUpload = ({
  value,

  onAddFile,
  // onConfirmDelete,
  onDeleteFile,
  onDownloadFile,

  sx,
  ...rest
}: CMFormFileUploadProps) => {
  const styles = useMultiStyleConfig("CMFormFileUploadStyles");

  const sectionId = useMemo(() => value.id, [value]);

  /**
   * ファイルアップロード時の処理
   * 呼び出し元にファイルがアップロードされた情報を返却する
   * @param subSectionIdx
   * @param addedFiles 追加されたファイル
   */
  const handleAddFile = async (subSectionIdx: number, addedFiles: File[]) => {
    for (const file of addedFiles) {
      onAddFile(sectionId, value.subSectionContents[subSectionIdx].id, file);
    }
  };

  /**
   * アップロード済みファイルの削除時の処理
   * 呼び出し元に削除されたファイルの情報を返却する
   */
  const handleDeleteFile = (
    subSectionIdx: number,
    itemContent: FileUploadItemContent,
  ) => {
    onDeleteFile?.(
      sectionId,
      value.subSectionContents[subSectionIdx].id,
      itemContent.id,
    );
  };

  const uploadButtonNode = (
    subSectionContent: FileUploadSubSectionContent,
    subSectionIdx: number,
    sx: SystemStyleObject,
  ) => {
    return (
      (subSectionContent.editMode ?? "editable") === "editable" && (
        <CMButtonFileUpload
          onUpload={(files: File[]) => {
            handleAddFile(subSectionIdx, files);
          }}
          accept={subSectionContent.accept}
          multiple={subSectionContent.multiple}
          sx={sx}
        >
          {subSectionContent.childrenButton}
        </CMButtonFileUpload>
      )
    );
  };
  const filesNode = (
    subSectionContent: FileUploadSubSectionContent,
    subSectionIdx: number,
  ) => (
    <Flex
      direction={"column"}
      sx={styles.filesArea}
      className={"subSectionContent"}
    >
      {subSectionContent.files.map((file, fileIdx) => (
        <CMFileUploadItem
          key={fileIdx}
          editMode={subSectionContent.editMode ?? "editable"}
          fileItem={file}
          messageAddition={subSectionContent.messageAddition}
          messageDelete={subSectionContent.messageDelete}
          // onConfirmDelete={onConfirmDelete}
          onDelete={() => {
            handleDeleteFile(subSectionIdx, file);
          }}
          onDownload={onDownloadFile}
          sx={styles.file}
        />
      ))}
    </Flex>
  );

  return (
    <VStack spacing="0" sx={{ ...styles.container, ...sx }} {...rest}>
      {/*セクションのタイトル表示エリア*/}
      {(value.title !== "" || hasValue(value.commentButtonProps)) && (
        <HStack sx={styles.sectionTitle}>
          <Text>{value.title}</Text>
          <CMFormControlHeader<string>
            value={null}
            commentButtonProps={value.commentButtonProps}
            commentsList={value.commentsList}
          />
        </HStack>
      )}
      {/*セクションに紐づくサブセクションを表示するエリア*/}
      <VStack sx={styles.sectionContainer} spacing={0}>
        {value.subSectionContents.map((subSectionContent, subSectionIdx) => (
          <Box key={subSectionIdx} sx={styles.sectionArea}>
            {/*必須入力表示エリア*/}
            <HStack>
              {subSectionContent.isRequired && (
                <Text sx={styles.messageRequired}>
                  <Icon as={GiPlainCircle} sx={styles.messageRequiredIcon} />
                  {subSectionContent.messageRequired}
                </Text>
              )}
            </HStack>

            {/*サブセクション*/}
            <Flex
              direction={"column"}
              sx={styles.subSection}
              className={"subSection"}
            >
              {hasValue(subSectionContent.title) ? (
                <>
                  {/*サブセクションタイトル*/}
                  <Flex
                    direction={"row"}
                    sx={styles.subSectionTitleArea}
                    className={"subSectionTitleArea"}
                  >
                    <CMFileUploadTitle
                      title={subSectionContent.title}
                      commentButtonProps={subSectionContent.commentButtonProps}
                      commentsList={subSectionContent.commentsList}
                      sx={styles.fileUploadTitle}
                    />
                    {uploadButtonNode(
                      subSectionContent,
                      subSectionIdx,
                      styles.uploadButton,
                    )}
                  </Flex>
                  {hasValue(subSectionContent.children) && (
                    <Box sx={styles.childrenArea}>
                      {subSectionContent.children}
                    </Box>
                  )}
                  {filesNode(subSectionContent, subSectionIdx)}
                </>
              ) : (
                <Flex
                  direction={"row"}
                  className={"HSTACK"}
                  alignItems={"flex-start"}
                >
                  <VStack
                    className={"VSTACK"}
                    alignItems={"stretch"}
                    flex={"1 1 max-content"}
                    minW={0}
                  >
                    {hasValue(subSectionContent.children) && (
                      <Box sx={styles.childrenArea}>
                        {subSectionContent.children}
                      </Box>
                    )}
                    {filesNode(subSectionContent, subSectionIdx)}
                  </VStack>
                  {uploadButtonNode(
                    subSectionContent,
                    subSectionIdx,
                    styles.uploadButtonNoTitle,
                  )}
                </Flex>
              )}
            </Flex>
          </Box>
        ))}
      </VStack>
    </VStack>
  );
};

export const CMFormFileUploadStyles: ComponentMultiStyleConfig = {
  parts: [
    "container",
    "sectionContainer",
    "sectionArea",
    "sectionTitle",
    "messageRequired",
    "messageRequiredIcon",
    "subSection",
    "subSectionTitleArea",
    "fileUploadTitle",
    "uploadButton",
    "uploadButtonNoTitle",
    "childrenArea",
    "filesArea",
    "files",
  ],
  baseStyle: {
    container: {
      alignItems: "stretch",
    },

    sectionContainer: {
      alignItems: "stretch",
      // position: "relative",
      pl: "8px",
      minH: "36px",
    },

    sectionArea: {
      minH: "36px",

      alignItems: "stretch",
      // position: "relative",

      borderWidth: "2px",
      borderTopStyle: "none",
      borderBottomStyle: "none",
      borderColor: "transparent",
      borderLeftColor: "gray.400",

      pl: "12px",
      display: "flex",
      flexDirection: "column",
    },

    sectionTitle: {
      alignItems: "flex-start",
    },

    messageRequired: {
      fontSize: "14px",
      color: "red.500",
    },

    messageRequiredIcon: {
      fontSize: "7px",
      marginRight: "3px",
      verticalAlign: "2px",
    },

    subSection: {
      alignItems: "stretch",
      mt: "4px",
      pl: "10px",
    },

    subSectionTitleArea: {
      alignSelf: "stretch",
    },

    fileUploadTitle: {
      flex: 1,
    },

    uploadButton: {
      w: "max-content",
      flex: "0 0 auto",
      ml: "auto",
    },

    uploadButtonNoTitle: {
      w: "max-content",
      flex: "0 0 auto",
      ml: "auto",
    },

    childrenArea: {
      display: "flex",
      flexFlow: "column nowrap",
      alignSelf: "stretch",
      alignItems: "flex-start",
      position: "relative",
      paddingLeft: "26px",
      marginTop: "0px",
    },

    filesArea: {
      alignItems: "stretch",
      gap: "4px",
      pl: "4px",
      pr: "8px",
    },

    file: {},
  },
};
